import React, { useState } from "react";
import {
  DateFieldComponent,
  ValidationRule,
} from "../../services/types/ComponentTypes";
import { toUTC } from "../../utils/dateUtils";

interface DateFieldProps {
  config: DateFieldComponent;
  value?: string;
  onChange?: (name: string, value: string) => void;
  externalError?: string;
}

export const DateField: React.FC<DateFieldProps> = ({
  config,
  value = "",
  onChange,
  externalError,
}) => {
  const [error, setError] = useState<string>("");
  const [isFocused, setIsFocused] = useState(false);

  const getTodayLocalMidnight = (): Date => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getYesterdayIso = (): string => {
    const d = getTodayLocalMidnight();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  // If PreventFutureDateSelection is enabled, DOB-like fields must be strictly before today.
  const preventTodayAndFuture = Boolean((config as any).PreventFutureDateSelection);

  const validateField = (inputValue: string): string => {
    if (!config.validation) return "";

    for (const rule of config.validation) {
      switch (rule.rule) {
        case ValidationRule.REQUIRED:
          if (!inputValue.trim()) return rule.message || "Date is required";
          break;
      }
    }

    // Additional date-specific validation
    if (inputValue) {
      const selectedDate = new Date(inputValue);
      const selectedDateOnly = new Date(selectedDate);
      selectedDateOnly.setHours(0, 0, 0, 0);

      if (config.minDate) {
        const minDate = new Date(config.minDate);
        if (selectedDate < minDate) {
          return `Date must be after ${formatDate(config.minDate)}`;
        }
      }

      // Handle maxDate validation
      if (config.maxDate) {
        if (config.maxDate === "today") {
          // For "today", compare with current date (without time)
          const today = getTodayLocalMidnight();
          
          if (selectedDateOnly > today) {
            return "Date cannot be a future date";
          }
        } else {
          // For specific date strings
          const maxDate = new Date(config.maxDate);
          if (selectedDate > maxDate) {
            return `Date must be before ${formatDate(config.maxDate)}`;
          }
        }
      }

      // Prevent selecting today or any future date (strictly before today)
      if (preventTodayAndFuture) {
        const today = getTodayLocalMidnight();
        if (selectedDateOnly >= today) {
          return "Date cannot be today or a future date";
        }
      }

      if (config.minDate == "today") {
        const minDate = new Date(config.minDate);
        if (selectedDate < minDate) {
          return `Date must be after ${formatDate(config.minDate)}`;
        }
      }
    }

    return "";
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value; // YYYY-MM-DD from browser input
    const validationError = validateField(newValue);
    setError(validationError);
    // Convert to ISO 8601 UTC for form/API, but pass empty string if cleared
    const isoValue = newValue ? (toUTC(newValue) || newValue) : "";
    onChange?.(config.name, isoValue);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const validationError = validateField(value);
    setError(validationError);
  };

  // Convert display format to HTML input format (always YYYY-MM-DD for input[type="date"])
  const convertToInputFormat = (dateValue: any): string => {
    if (!dateValue) return "";

    // If it's already a Date object
    if (dateValue instanceof Date) {
      if (!isNaN(dateValue.getTime())) {
        return dateValue.toISOString().split("T")[0];
      }
      return "";
    }

    const strValue = String(dateValue).trim();
    if (!strValue) return "";

    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(strValue)) {
      return strValue;
    }

    // Handle DD-MM-YYYY format (with optional time)
    const ddmmyyyyMatch = strValue.match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
    if (ddmmyyyyMatch) {
      const day = ddmmyyyyMatch[1].padStart(2, "0");
      const month = ddmmyyyyMatch[2].padStart(2, "0");
      const year = ddmmyyyyMatch[3];
      return `${year}-${month}-${day}`;
    }

    // Handle DD/MM/YYYY format
    const slashMatch = strValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
      const day = slashMatch[1].padStart(2, "0");
      const month = slashMatch[2].padStart(2, "0");
      const year = slashMatch[3];
      return `${year}-${month}-${day}`;
    }

    // Try to parse and convert other formats
    const date = new Date(strValue);
    if (!isNaN(date.getTime())) {
      // Use local date part to avoid timezone shifts
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    return "";
  };

  const getWidthClass = () => {
    if (config.style?.widthMode === "wrap") return "w-auto";
    return "w-full";
  };

  const inputStyles: React.CSSProperties = {
    borderRadius: config.style?.borderRadius || 8,
    fontSize: config.style?.fontSize || 14, // Standardized to 14px (text-sm)
    fontWeight: config.style?.fontWeight || "normal",
    // Only set color/backgroundColor if explicitly provided, otherwise let Tailwind handle it
    ...(config.style?.textColor && !config.disabled ? { color: config.style.textColor } : {}),
    ...(config.style?.backgroundColor && !config.disabled ? { backgroundColor: config.style.backgroundColor } : {}),
    margin: config.style?.margin || 0,
    padding: config.style?.padding || undefined, // Use Tailwind padding
    fontFamily: "inherit", // Use system font stack
    textAlign: config.style?.textAlign || "left",
  };

  return (
    <div className={`${getWidthClass()} px-1`} data-field-name={config.name}>
      {config.label && (
        <label className="block mb-0.5 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
          {config.label}
          {config.required && <span className="text-danger-500 dark:text-danger-400 ml-1">*</span>}
        </label>
      )}

      <div className="relative inline-block w-full">
        <input
          type="date"
          name={config.name}
          value={convertToInputFormat(value)}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          disabled={config.disabled}
          min={(config.minDate == "today") ? new Date().toISOString().split("T")[0] : config.minDate}
          max={
            preventTodayAndFuture
              ? getYesterdayIso()
              : (config.maxDate == "today")
                ? new Date().toISOString().split("T")[0]
                : config.maxDate
          }
          className={`w-full py-2 px-3 text-sm font-normal text-left border border-1 outline-none transition-colors duration-200 font-sans dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 ${config.disabled ? "cursor-not-allowed opacity-70 bg-gray-100 dark:bg-gray-700" : "cursor-pointer"
            } ${error
              ? "border-danger-500 dark:border-danger-500"
              : isFocused
                ? "border-primary-500 dark:border-primary-400"
                : "border-gray-300 dark:border-gray-600"
            } focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none`}
          style={inputStyles}
        />
        {/* <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-500 text-lg">
          📅
        </span> */}
      </div>

      {(error || externalError) && (
        <div className="text-danger-500 dark:text-danger-400 text-xs mt-1">
          {error || externalError}
        </div>
      )}

      {config.helperText && !error && !externalError && (
        <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">{config.helperText}</div>
      )}

      {config.dateFormat && (
        <div className="text-gray-400 dark:text-gray-500 text-xs mt-0.5 italic">
          Format: {config.dateFormat}
        </div>
      )}
    </div>
  );
};
